/**
 * Contratos de entrada (Zod) de la facturación electrónica: configuración
 * FEL de la institución y filtros del listado de facturas.
 */
import { z } from 'zod';
import { FEL_CERTIFIERS, FEL_DOCUMENT_STATUSES, FEL_DTE_TYPES, FEL_EXEMPTION_SCENARIOS, FEL_IVA_AFFILIATIONS, FEL_PHRASES, } from '../billing/catalog.js';
import { dateRangeQuerySchema, idSchema, listQuerySchema } from './common.js';
/** Límites de la configuración FEL. */
const FEL_LIMITS = {
    /** Código de establecimiento del RTU: entero positivo de hasta 3 dígitos. */
    establishmentCodeMax: 999,
    /** Largo máximo del nombre comercial en el DTE. */
    commercialNameMax: 150,
    /** Largo de municipio y departamento. */
    placeMax: 60,
    /** Frases generales que se pueden declarar a la vez. */
    phrasesMax: 5,
};
/** Código postal guatemalteco de cinco dígitos. */
const POSTAL_CODE_PATTERN = /^\d{5}$/;
/** Frase general del catálogo que Tumin conoce. */
const phraseSchema = z
    .object({ type: z.number().int().positive(), scenario: z.number().int().positive() })
    .refine((phrase) => FEL_PHRASES.some((known) => known.type === phrase.type && known.scenario === phrase.scenario), {
    message: 'Frase no reconocida en el catálogo de la SAT',
});
/** Escenarios de exención que acepta la configuración. */
const EXEMPTION_SCENARIO_CODES = FEL_EXEMPTION_SCENARIOS.map((scenario) => scenario.scenario);
/** Configuración de la facturación electrónica (sin secretos). */
export const FelSettingsSchema = z.object({
    enabled: z.boolean(),
    certifier: z.enum(FEL_CERTIFIERS),
    commercialName: z.string().trim().min(2, 'Ingresa el nombre comercial').max(FEL_LIMITS.commercialNameMax),
    establishmentCode: z.number().int().min(1).max(FEL_LIMITS.establishmentCodeMax),
    ivaAffiliation: z.enum(FEL_IVA_AFFILIATIONS),
    phrases: z.array(phraseSchema).max(FEL_LIMITS.phrasesMax),
    exemptionScenario: z
        .number()
        .int()
        .refine((scenario) => EXEMPTION_SCENARIO_CODES.includes(scenario), 'Escenario de exención no reconocido'),
    emailCopy: z.boolean(),
    address: z.object({
        postalCode: z.string().trim().regex(POSTAL_CODE_PATTERN, 'Código postal de 5 dígitos'),
        municipality: z.string().trim().min(2).max(FEL_LIMITS.placeMax),
        department: z.string().trim().min(2).max(FEL_LIMITS.placeMax),
    }),
});
/** Filtros del listado de facturas electrónicas. */
export const invoiceListQuerySchema = listQuerySchema
    .merge(dateRangeQuerySchema)
    .extend({
    status: z.enum(FEL_DOCUMENT_STATUSES).optional(),
    dteType: z.enum(FEL_DTE_TYPES).optional(),
    branchId: idSchema.optional(),
    paymentId: idSchema.optional(),
})
    .refine((query) => !query.from || !query.to || query.from <= query.to, {
    message: 'La fecha inicial no puede ser posterior a la final',
    path: ['from'],
});
//# sourceMappingURL=billing.js.map